import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BadgePercent, Check } from "lucide-react";

import { cn } from "@/lib/utils";
import type { PartnerBenefitStatus, TenantPlanOption } from "@/services/authService";
import {
  catalogPlanPrice,
  isAnnualOfferedToTenant,
  isTenantPlanDiscounted,
  tenantPlanPrice,
} from "@/lib/tenant-plan-pricing";
import { computePartnerBenefitCopy } from "@/lib/partner-benefit-copy";

export interface PlanStepProps {
  plans: TenantPlanOption[];
  /** Null when the tenant has no active partner redemption to explain. */
  partnerBenefit: PartnerBenefitStatus | null;
  selectedPlanId: string | null;
  selectedBillingInterval: "monthly" | "annual";
  setSelectedBillingInterval: (v: "monthly" | "annual") => void;
  onSelectPlan: (planId: string) => void;
  /**
   * Server-computed plans this tenant may self-select for their régimen
   * (`tenant.eligiblePlanIds`, from `resolveEligiblePlanIds`). Same filter the
   * settings ChangePlanModal uses. Null = not loaded yet (show all). The régimen
   * declaration is now captured on its own step BEFORE this one, so by the time
   * these render the list is already tailored to the answer.
   */
  eligiblePlanIds: string[] | null;
}

// The recommended tier (badge + default preselect). Must be the ACTIVE catalog
// id 'ombu' — the legacy 'pro' id it used to hold only exists for grandfathered
// subscribers, so new tenants never matched it and the badge/preselect no-opped.
const FEATURED_PLAN_ID = "ombu";

function formatPrice(uyu: number): string {
  return uyu.toLocaleString("es-UY");
}

export function PlanStep({
  plans,
  partnerBenefit,
  selectedPlanId,
  selectedBillingInterval,
  setSelectedBillingInterval,
  onSelectPlan,
  eligiblePlanIds,
}: PlanStepProps): JSX.Element {
  const { t } = useTranslation();
  // Show only the plans eligible for this tenant's régimen (or the current
  // selection), the same rule as the settings ChangePlanModal. Null = not loaded.
  const visiblePlans = plans.filter(
    (p) =>
      eligiblePlanIds == null ||
      eligiblePlanIds.includes(p.id) ||
      p.id === selectedPlanId,
  );
  const partnerBenefitCopy = partnerBenefit
    ? computePartnerBenefitCopy(t, partnerBenefit)
    : null;
  // The 80 UI credit accrues monthly, so a tenant receiving it is billed monthly
  // only and the server withholds tenantPriceAnnual for their billable tiers.
  // Hide the cycle toggle rather than offer one the checkout would reject.
  const annualOffered = isAnnualOfferedToTenant(plans);
  const annual = annualOffered && selectedBillingInterval === "annual";

  useEffect(() => {
    if (!annualOffered && selectedBillingInterval === "annual") {
      setSelectedBillingInterval("monthly");
    }
  }, [annualOffered, selectedBillingInterval, setSelectedBillingInterval]);

  // Preselect the top eligible plan when the user lands here with none chosen —
  // the featured (Ombú) if it's eligible, else the first eligible card (e.g. a
  // Literal E tenant whose only option is the literalE plan). Anchors without
  // forcing; they can still click another card.
  useEffect(() => {
    if (selectedPlanId) return;
    if (visiblePlans.length === 0) return;
    const preferred =
      visiblePlans.find((p) => p.id === FEATURED_PLAN_ID) ?? visiblePlans[0];
    onSelectPlan(preferred.id);
  }, [selectedPlanId, visiblePlans, onSelectPlan]);

  return (
    <div>
      <div className="fx-onb-plans-head">
        <div className="left">
          <h2 className="fx-onb-h1" style={{ marginBottom: 12 }}>
            {t("onboarding.plansStep.h1Lead", "Elegí cómo querés")}{" "}
            <span className="ital">
              {t("onboarding.plansStep.h1Accent", "arrancar.")}
            </span>
          </h2>
          <p className="fx-onb-lead">
            {t(
              "onboarding.plansStep.lead",
              "Empezás gratis. Cambiás de plan cuando quieras. Sin tarjeta para arrancar.",
            )}
          </p>
        </div>
        {annualOffered ? (
        <div className="fx-onb-billing-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={!annual}
            className={cn(!annual && "active")}
            onClick={() => setSelectedBillingInterval("monthly")}
          >
            {t("settings.plans.monthly", "Mensual")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={annual}
            className={cn(annual && "active")}
            onClick={() => setSelectedBillingInterval("annual")}
          >
            {t("settings.plans.annual", "Anual")}
            <span className="savings">
              {t("onboarding.plansStep.savings", "2 meses gratis")}
            </span>
          </button>
        </div>
        ) : null}
      </div>

      {partnerBenefitCopy ? (
        <div className="fx-onb-partner-banner">
          <span className="icon-circle">
            <BadgePercent aria-hidden />
          </span>
          <div className="text">
            <div className="title">
              {t("partnerBenefit.bannerTitle", "Tenés el beneficio de {{partner}} activo", {
                partner: partnerBenefit?.partnerName,
              })}
            </div>
            <p className="detail">
              {partnerBenefitCopy.durationSentence}
              {partnerBenefitCopy.nextStageSentence
                ? ` ${partnerBenefitCopy.nextStageSentence}`
                : ""}
            </p>
          </div>
        </div>
      ) : null}

      <div className="fx-onb-plans-grid">
        {visiblePlans.map((plan) => {
          const isFeatured = plan.id === FEATURED_PLAN_ID;
          const isSelected = selectedPlanId === plan.id;
          // Tenant price, not catalog price — an Art. 5 tenant's 80 UI credit is
          // already deducted here, and `isFree` routes the activation path.
          const interval = annual ? "annual" : "monthly";
          const price = tenantPlanPrice(plan, interval);
          const isFree = price === 0;
          // Neither discount is ever applied annually (both require a monthly
          // cycle — see BENEFIT_BILLING_INTERVAL / the partner payment-link
          // cron), so there's nothing to attribute once annual is selected.
          const isDiscounted = !annual && isTenantPlanDiscounted(plan, interval);
          const catalogPrice = catalogPlanPrice(plan, interval);
          const hasFiscalBenefit = !annual && plan.fiscalBenefitDiscount > 0;
          const hasPartnerBenefit =
            !annual && (plan.partnerDiscount ?? 0) > 0 && partnerBenefit != null;
          const tagline = t(`settings.plans.${plan.id}.tagline`, "");

          const bullets = t(`settings.plans.${plan.id}.descriptionBullets`, {
            returnObjects: true,
          });
          const features = Array.isArray(bullets)
            ? bullets.map((b) => String(b))
            : [];

          return (
            <button
              key={plan.id}
              type="button"
              className={cn(
                "fx-onb-plan-card",
                isFeatured && "featured",
                isSelected && "selected",
              )}
              onClick={() => onSelectPlan(plan.id)}
              aria-pressed={isSelected}
            >
              {isFeatured ? (
                <span className="recommended-badge">
                  {t("onboarding.plansStep.recommended", "Recomendado")}
                </span>
              ) : null}

              <div className="plan-head">
                <div className="name">{t(plan.nameKey)}</div>
                {tagline && tagline !== `settings.plans.${plan.id}.tagline` ? (
                  <div className="desc">{tagline}</div>
                ) : (
                  <div className="desc">{t(plan.descriptionKey)}</div>
                )}
              </div>

              <div className="plan-price">
                {isFree ? (
                  <>
                    <span className="free">
                      {t("settings.plans.freeLabel", "Gratis")}
                    </span>
                    <span className="free-sub">
                      {t("onboarding.plansStep.freeSuffix", "para siempre")}
                    </span>
                  </>
                ) : (
                  <>
                    {isDiscounted ? (
                      <span className="was">{formatPrice(catalogPrice)}</span>
                    ) : null}
                    <span className="prefix">UYU</span>
                    <span className="amount">{formatPrice(price)}</span>
                    <span className="suffix">
                      /{" "}
                      {annual
                        ? t("settings.plans.perYear", "año")
                        : t("settings.plans.perMonth", "mes")}
                    </span>
                    <span className="plus-iva">
                      {t("settings.plans.plusIva", "+ IVA")}
                    </span>
                  </>
                )}
              </div>

              {hasFiscalBenefit || hasPartnerBenefit ? (
                <div className="discount-reasons">
                  {hasPartnerBenefit ? (
                    <span className="discount-reason partner">
                      <BadgePercent aria-hidden />
                      {t(
                        "partnerBenefit.cardTag",
                        "Incluye tu beneficio de {{partner}}",
                        { partner: partnerBenefit?.partnerName },
                      )}
                    </span>
                  ) : null}
                  {hasFiscalBenefit ? (
                    <span className="discount-reason fiscal">
                      {t(
                        "settings.plans.fiscalBenefitTag",
                        "Incluye crédito fiscal (80 UI)",
                      )}
                    </span>
                  ) : null}
                </div>
              ) : null}

              <ul className="plan-features">
                {features.map((feature, i) => (
                  <li key={i}>
                    <span className="check" aria-hidden>
                      <Check />
                    </span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>

    </div>
  );
}
